import { Node } from '@xyflow/react';
import type { Site } from '@/types/site';
import type { Zone } from '@/types/zone';
import type { System } from '@/types/system';
import type { CloudService } from '@/types/cloudService';
import type { CloudResource } from '@/types/cloudResource';
import type { Endpoint } from '@/types/endpoint';
import type { InternetConnection } from '@/types/internetConnection';
import type { DMZConfig } from '@/types/dmzConfig';

// ============= LAYOUT CONFIGURATION =============
// Centralized spacing constants for consistent, beautiful layouts
export const LAYOUT_CONFIG = {
  // Canvas margins
  canvasMargin: 80,
  
  // Container spacing (between top-level siblings)
  containerGap: 120,        // Gap between Sites / Cloud Services container
  sitesPerRow: 3,           // Max sites per row before wrapping
  
  // Internal padding (inside containers - space from edge to children)
  sitePadding: { top: 70, left: 40, right: 40, bottom: 40 },
  zonePadding: { top: 55, left: 30, right: 30, bottom: 30 },
  systemPadding: { top: 50, left: 25, right: 25, bottom: 25 },
  cloudServicePadding: { top: 60, left: 30, right: 30, bottom: 30 },
  
  // Child spacing (between children inside container)
  zoneGap: 35,              // Horizontal gap between zones inside a site
  systemGap: 30,            // Horizontal gap between systems inside a zone
  endpointGap: 20,          // Gap between endpoints inside a system
  cloudServiceGap: 40,      // Gap between cloud services inside cloud container
  cloudResourceGap: 25,     // Gap between cloud resources
  
  // Minimum container sizes (ensures containers look good even when empty)
  minSiteWidth: 450,
  minSiteHeight: 350,
  minZoneWidth: 300,
  minZoneHeight: 220,
  minSystemWidth: 180,
  minSystemHeight: 140,
  minCloudServiceWidth: 280,
  minCloudServiceHeight: 200,
  
  // Node sizes (fixed dimensions for leaf nodes)
  endpointSize: { width: 130, height: 65 },
  cloudResourceSize: { width: 160, height: 80 },
  internetNodeSize: { width: 140, height: 70 },
  dmzNodeSize: { width: 150, height: 75 },
  
  // Grid configuration for endpoints
  endpointsPerRow: 3,       // Max endpoints per row inside a system
  cloudResourcesPerRow: 3,  // Max cloud resources per row
};

interface GenerateContainerLayoutOptions {
  sites: Site[];
  zones: Zone[];
  systems: System[];
  cloudServices?: CloudService[];
  cloudResources?: CloudResource[];
  endpoints?: Endpoint[];
  internetConnections?: InternetConnection[];
  dmzConfigs?: DMZConfig[];
  existingNodes?: Node[];
}

interface CalculatedSize {
  width: number;
  height: number;
}

// ============= SIZE CALCULATION FUNCTIONS =============
// Bottom-up approach: calculate from endpoints → systems → zones → sites

function calculateSystemSize(endpointCount: number): CalculatedSize {
  const { systemPadding, endpointSize, endpointGap, endpointsPerRow, minSystemWidth, minSystemHeight } = LAYOUT_CONFIG;
  
  if (endpointCount === 0) {
    return { width: minSystemWidth, height: minSystemHeight };
  }
  
  const cols = Math.min(endpointCount, endpointsPerRow);
  const rows = Math.ceil(endpointCount / endpointsPerRow);
  
  const contentWidth = cols * endpointSize.width + (cols - 1) * endpointGap;
  const contentHeight = rows * endpointSize.height + (rows - 1) * endpointGap;
  
  const width = systemPadding.left + contentWidth + systemPadding.right;
  const height = systemPadding.top + contentHeight + systemPadding.bottom;
  
  return {
    width: Math.max(width, minSystemWidth),
    height: Math.max(height, minSystemHeight),
  };
}

function calculateZoneSize(systemSizes: CalculatedSize[]): CalculatedSize {
  const { zonePadding, systemGap, minZoneWidth, minZoneHeight } = LAYOUT_CONFIG;
  
  if (systemSizes.length === 0) {
    return { width: minZoneWidth, height: minZoneHeight };
  }
  
  // Systems laid out horizontally
  const contentWidth = systemSizes.reduce((sum, s) => sum + s.width, 0) + (systemSizes.length - 1) * systemGap;
  const contentHeight = Math.max(...systemSizes.map(s => s.height));
  
  const width = zonePadding.left + contentWidth + zonePadding.right;
  const height = zonePadding.top + contentHeight + zonePadding.bottom;
  
  return {
    width: Math.max(width, minZoneWidth),
    height: Math.max(height, minZoneHeight),
  };
}

function calculateSiteSize(zoneSizes: CalculatedSize[]): CalculatedSize {
  const { sitePadding, zoneGap, minSiteWidth, minSiteHeight } = LAYOUT_CONFIG;
  
  if (zoneSizes.length === 0) {
    return { width: minSiteWidth, height: minSiteHeight };
  }
  
  // Zones laid out horizontally
  const contentWidth = zoneSizes.reduce((sum, z) => sum + z.width, 0) + (zoneSizes.length - 1) * zoneGap;
  const contentHeight = Math.max(...zoneSizes.map(z => z.height));
  
  const width = sitePadding.left + contentWidth + sitePadding.right;
  const height = sitePadding.top + contentHeight + sitePadding.bottom;
  
  return {
    width: Math.max(width, minSiteWidth),
    height: Math.max(height, minSiteHeight),
  };
}

function calculateCloudServiceSize(resourceCount: number): CalculatedSize {
  const { cloudServicePadding, cloudResourceSize, cloudResourceGap, cloudResourcesPerRow, minCloudServiceWidth, minCloudServiceHeight } = LAYOUT_CONFIG;
  
  if (resourceCount === 0) {
    return { width: minCloudServiceWidth, height: minCloudServiceHeight };
  }
  
  const cols = Math.min(resourceCount, cloudResourcesPerRow);
  const rows = Math.ceil(resourceCount / cloudResourcesPerRow);
  
  const contentWidth = cols * cloudResourceSize.width + (cols - 1) * cloudResourceGap;
  const contentHeight = rows * cloudResourceSize.height + (rows - 1) * cloudResourceGap;
  
  const width = cloudServicePadding.left + contentWidth + cloudServicePadding.right;
  const height = cloudServicePadding.top + contentHeight + cloudServicePadding.bottom;
  
  return {
    width: Math.max(width, minCloudServiceWidth),
    height: Math.max(height, minCloudServiceHeight),
  };
}

function calculateCloudContainerSize(serviceSizes: CalculatedSize[]): CalculatedSize {
  const { sitePadding, cloudServiceGap } = LAYOUT_CONFIG;
  
  if (serviceSizes.length === 0) {
    return { width: 400, height: 300 };
  }
  
  // Cloud services laid out horizontally
  const contentWidth = serviceSizes.reduce((sum, s) => sum + s.width, 0) + (serviceSizes.length - 1) * cloudServiceGap;
  const contentHeight = Math.max(...serviceSizes.map(s => s.height));
  
  return {
    width: sitePadding.left + contentWidth + sitePadding.right,
    height: sitePadding.top + contentHeight + sitePadding.bottom,
  };
}

/**
 * Generates React Flow nodes as nested containers (Groups) with optimized spacing
 * Sites/CloudServices contain Zones, Zones contain Systems, Systems contain Endpoints
 * Also generates Internet and DMZ nodes within their parent containers
 */
export function generateContainerLayout({
  sites,
  zones,
  systems,
  cloudServices = [],
  cloudResources = [],
  endpoints = [],
  internetConnections = [],
  dmzConfigs = [],
  existingNodes = [],
}: GenerateContainerLayoutOptions): Node[] {
  const newNodes: Node[] = [];
  const { canvasMargin, containerGap, sitesPerRow, sitePadding, zonePadding, systemPadding, zoneGap, systemGap, endpointGap, cloudServiceGap, cloudResourceGap, endpointSize, cloudResourceSize, internetNodeSize, dmzNodeSize, endpointsPerRow, cloudResourcesPerRow, cloudServicePadding } = LAYOUT_CONFIG;
  
  // Helper to get existing node with full state (preserves manual positioning)
  const getExistingNode = (id: string) => existingNodes.find(n => n.id === id);

  // ============= PRE-CALCULATE ALL SIZES =============
  // This bottom-up calculation ensures containers fit their content
  
  const systemSizeMap = new Map<string, CalculatedSize>();
  const zoneSizeMap = new Map<string, CalculatedSize>();
  const siteSizeMap = new Map<string, CalculatedSize>();
  const cloudServiceSizeMap = new Map<string, CalculatedSize>();
  
  // Calculate system sizes based on endpoints
  systems.forEach(system => {
    const systemEndpoints = endpoints.filter(e => e.system_id === system.id);
    systemSizeMap.set(system.id, calculateSystemSize(systemEndpoints.length));
  });
  
  // Calculate zone sizes based on systems
  zones.forEach(zone => {
    const zoneSystems = systems.filter(s => s.zone_id === zone.id);
    const systemSizes = zoneSystems.map(s => systemSizeMap.get(s.id) || calculateSystemSize(0));
    zoneSizeMap.set(zone.id, calculateZoneSize(systemSizes));
  });
  
  // Calculate site sizes based on zones
  sites.forEach(site => {
    const siteZones = zones.filter(z => z.site_id === site.id);
    const zoneSizes = siteZones.map(z => zoneSizeMap.get(z.id) || calculateZoneSize([]));
    siteSizeMap.set(site.id, calculateSiteSize(zoneSizes));
  });
  
  // Calculate cloud service sizes based on resources
  cloudServices.forEach(service => {
    const serviceResources = cloudResources.filter(r => r.cloud_service_id === service.id);
    cloudServiceSizeMap.set(service.id, calculateCloudServiceSize(serviceResources.length));
  });

  // ============= CLOUD SERVICES SECTION =============
  let cloudContainerHeight = 0;
  
  if (cloudServices.length > 0) {
    const cloudParentId = 'cloudServices';
    const existingCloudParent = getExistingNode(cloudParentId);
    
    // Calculate cloud container size from services
    const serviceSizes = cloudServices.map(s => cloudServiceSizeMap.get(s.id) || calculateCloudServiceSize(0));
    const cloudContainerSize = calculateCloudContainerSize(serviceSizes);
    cloudContainerHeight = cloudContainerSize.height;
    
    // Create cloud services parent container
    newNodes.push({
      id: cloudParentId,
      type: 'cloudGroup',
      position: existingCloudParent?.position || { x: canvasMargin, y: canvasMargin },
      data: existingCloudParent?.data || {
        id: cloudParentId,
        label: 'Cloud Services',
        provider: 'multiple',
        region: '',
      },
      style: existingCloudParent?.style || { width: cloudContainerSize.width, height: cloudContainerSize.height },
    });

    // Add individual cloud services as children
    let cloudXOffset = sitePadding.left;
    cloudServices.forEach((cloud) => {
      const cloudNodeId = `cloud-${cloud.id}`;
      const existingCloud = getExistingNode(cloudNodeId);
      const serviceSize = cloudServiceSizeMap.get(cloud.id) || calculateCloudServiceSize(0);
      
      // Use existing position or calculated position
      const cloudPosition = existingCloud?.position || { x: cloudXOffset, y: sitePadding.top };
      const cloudStyle = existingCloud?.style || { width: serviceSize.width, height: serviceSize.height };

      newNodes.push({
        id: cloudNodeId,
        type: 'cloudGroup',
        position: cloudPosition,
        parentId: cloudParentId,
        extent: 'parent',
        data: {
          id: cloud.id,
          label: cloud.name,
          provider: cloud.provider,
          region: cloud.region,
        },
        style: cloudStyle,
      } as Node);

      // Generate cloud resources in grid layout
      const serviceCloudResources = cloudResources.filter(r => r.cloud_service_id === cloud.id);
      serviceCloudResources.forEach((resource, idx) => {
        const resourceNodeId = `cloudResource-${resource.id}`;
        const existingResource = getExistingNode(resourceNodeId);
        
        const col = idx % cloudResourcesPerRow;
        const row = Math.floor(idx / cloudResourcesPerRow);
        
        const defaultPosition = {
          x: cloudServicePadding.left + col * (cloudResourceSize.width + cloudResourceGap),
          y: cloudServicePadding.top + row * (cloudResourceSize.height + cloudResourceGap),
        };

        newNodes.push({
          id: resourceNodeId,
          type: 'cloudResource',
          position: existingResource?.position || (resource.position_x != null ? { x: resource.position_x, y: resource.position_y || defaultPosition.y } : defaultPosition),
          parentId: cloudNodeId,
          extent: 'parent',
          data: {
            id: resource.id,
            label: resource.name,
            type: 'cloudResource',
            category: resource.resource_type,
            cloud_service_id: resource.cloud_service_id,
            status: resource.status,
            criticality: resource.criticality,
          },
          style: { width: cloudResourceSize.width, height: cloudResourceSize.height },
        } as Node);
      });

      cloudXOffset += serviceSize.width + cloudServiceGap;
    });
  }

  // ============= PHYSICAL SITES SECTION (Grid Layout) =============
  // Position sites in a grid pattern (3 per row by default)
  
  const sitesStartY = cloudContainerHeight > 0 ? canvasMargin + cloudContainerHeight + containerGap : canvasMargin;
  const sitesList = [...sites]; // Copy for ordering
  
  // Calculate row heights for proper vertical positioning
  const rowHeights: number[] = [];
  for (let row = 0; row * sitesPerRow < sitesList.length; row++) {
    const rowSites = sitesList.slice(row * sitesPerRow, (row + 1) * sitesPerRow);
    const maxHeight = Math.max(...rowSites.map(s => siteSizeMap.get(s.id)?.height || LAYOUT_CONFIG.minSiteHeight));
    rowHeights.push(maxHeight);
  }

  sitesList.forEach((site, siteIndex) => {
    const siteNodeId = `site-${site.id}`;
    const existingSite = getExistingNode(siteNodeId);
    const siteZones = zones.filter(z => z.site_id === site.id);
    const siteSize = siteSizeMap.get(site.id) || calculateSiteSize([]);
    
    // Calculate grid position
    const row = Math.floor(siteIndex / sitesPerRow);
    const col = siteIndex % sitesPerRow;
    
    // Calculate X position based on previous columns in same row
    let siteX = canvasMargin;
    for (let c = 0; c < col; c++) {
      const prevSiteIndex = row * sitesPerRow + c;
      if (prevSiteIndex < sitesList.length) {
        const prevSize = siteSizeMap.get(sitesList[prevSiteIndex].id) || { width: LAYOUT_CONFIG.minSiteWidth };
        siteX += prevSize.width + containerGap;
      }
    }
    
    // Calculate Y position based on previous rows
    let siteY = sitesStartY;
    for (let r = 0; r < row; r++) {
      siteY += rowHeights[r] + containerGap;
    }
    
    // Use existing position for existing sites, calculated for new
    const sitePosition = existingSite?.position || { x: siteX, y: siteY };
    const siteStyle = existingSite?.style || { width: siteSize.width, height: siteSize.height };

    newNodes.push({
      id: siteNodeId,
      type: 'siteGroup',
      position: sitePosition,
      data: {
        id: site.id,
        label: site.name,
        criticality: site.criticality,
        siteCode: site.site_code,
      },
      style: siteStyle,
    });

    // ============= ZONES INSIDE SITE =============
    let zoneXOffset = sitePadding.left;
    siteZones.forEach((zone) => {
      const zoneNodeId = `zone-${zone.id}`;
      const existingZone = getExistingNode(zoneNodeId);
      const zoneSystems = systems.filter(s => s.zone_id === zone.id);
      const zoneSize = zoneSizeMap.get(zone.id) || calculateZoneSize([]);

      const zonePosition = existingZone?.position || { x: zoneXOffset, y: sitePadding.top };
      const zoneStyle = existingZone?.style || { width: zoneSize.width, height: zoneSize.height };

      newNodes.push({
        id: zoneNodeId,
        type: 'zoneGroup',
        position: zonePosition,
        data: {
          id: zone.id,
          label: zone.name,
          domain: zone.domain,
          purdueLevel: zone.purdue_level,
        },
        parentId: siteNodeId,
        extent: 'parent',
        style: zoneStyle,
      } as Node);

      // ============= SYSTEMS INSIDE ZONE =============
      let systemXOffset = zonePadding.left;
      zoneSystems.forEach((system) => {
        const systemNodeId = `system-${system.id}`;
        const existingSystem = getExistingNode(systemNodeId);
        const systemEndpoints = endpoints.filter(e => e.system_id === system.id);
        const systemSize = systemSizeMap.get(system.id) || calculateSystemSize(0);

        const systemPosition = existingSystem?.position || { x: systemXOffset, y: zonePadding.top };
        const systemStyle = existingSystem?.style || { width: systemSize.width, height: systemSize.height };

        newNodes.push({
          id: systemNodeId,
          type: 'systemGroup',
          position: systemPosition,
          data: {
            id: system.id,
            label: system.name,
            function: system.function,
          },
          parentId: zoneNodeId,
          extent: 'parent',
          style: systemStyle,
        } as Node);

        // ============= ENDPOINTS INSIDE SYSTEM (Grid Layout) =============
        systemEndpoints.forEach((endpoint, idx) => {
          const endpointNodeId = `endpoint-${endpoint.id}`;
          const existingEndpoint = getExistingNode(endpointNodeId);
          
          const col = idx % endpointsPerRow;
          const row = Math.floor(idx / endpointsPerRow);
          
          const defaultPosition = {
            x: systemPadding.left + col * (endpointSize.width + endpointGap),
            y: systemPadding.top + row * (endpointSize.height + endpointGap),
          };

          const endpointPosition = existingEndpoint?.position || 
            (endpoint.position_x != null ? { x: endpoint.position_x, y: endpoint.position_y || defaultPosition.y } : defaultPosition);

          newNodes.push({
            id: endpointNodeId,
            type: 'endpoint',
            position: endpointPosition,
            parentId: systemNodeId,
            extent: 'parent',
            data: {
              id: endpoint.id,
              label: endpoint.name,
              type: 'endpoint',
              category: endpoint.asset_type || endpoint.category,
              ip: endpoint.ip,
              system_id: endpoint.system_id,
            },
            style: { width: endpointSize.width, height: endpointSize.height },
          } as Node);
        });

        systemXOffset += systemSize.width + systemGap;
      });

      zoneXOffset += zoneSize.width + zoneGap;
    });

    // ============= INTERNET CONNECTIONS FOR THIS SITE =============
    const siteInternetConnections = internetConnections.filter(ic => ic.site_id === site.id && !ic.zone_id && !ic.system_id);
    siteInternetConnections.forEach((connection, idx) => {
      const internetNodeId = `internet-${connection.id}`;
      const existingInternet = getExistingNode(internetNodeId);
      
      // Position at the top-right of the site
      const defaultPosition = {
        x: (siteSizeMap.get(site.id)?.width || LAYOUT_CONFIG.minSiteWidth) - internetNodeSize.width - 20 - (idx * (internetNodeSize.width + 15)),
        y: sitePadding.top - 10,
      };

      newNodes.push({
        id: internetNodeId,
        type: 'internet',
        position: existingInternet?.position || (connection.position_x != null ? { x: connection.position_x, y: connection.position_y || defaultPosition.y } : defaultPosition),
        parentId: siteNodeId,
        extent: 'parent',
        data: {
          id: connection.id,
          connectionId: connection.id,
          label: connection.label,
          connectionType: connection.connection_type || 'Fiber',
          supplier: connection.supplier,
        },
        style: { width: internetNodeSize.width, height: internetNodeSize.height },
      } as Node);
    });

    // ============= DMZ CONFIGS FOR THIS SITE =============
    const siteDMZConfigs = dmzConfigs.filter(dc => dc.site_id === site.id && !dc.zone_id && !dc.system_id && !dc.cloud_service_id);
    siteDMZConfigs.forEach((config, idx) => {
      const dmzNodeId = `dmz-${config.id}`;
      const existingDMZ = getExistingNode(dmzNodeId);
      
      // Position at the left side of the site
      const defaultPosition = {
        x: sitePadding.left + (idx * (dmzNodeSize.width + 15)),
        y: (siteSizeMap.get(site.id)?.height || LAYOUT_CONFIG.minSiteHeight) - dmzNodeSize.height - 20,
      };

      newNodes.push({
        id: dmzNodeId,
        type: 'dmz',
        position: existingDMZ?.position || (config.position_x != null ? { x: config.position_x, y: config.position_y || defaultPosition.y } : defaultPosition),
        parentId: siteNodeId,
        extent: 'parent',
        data: {
          id: config.id,
          configId: config.id,
          label: config.label,
          firewallRules: config.rules?.length || 0,
        },
        style: { width: dmzNodeSize.width, height: dmzNodeSize.height },
      } as Node);
    });
  });

  // ============= DMZ CONFIGS FOR CLOUD SERVICES =============
  cloudServices.forEach((cloudService) => {
    const cloudNodeId = `cloud-${cloudService.id}`;
    const cloudDMZConfigs = dmzConfigs.filter(dc => dc.cloud_service_id === cloudService.id);
    
    cloudDMZConfigs.forEach((config, idx) => {
      const dmzNodeId = `dmz-${config.id}`;
      const existingDMZ = getExistingNode(dmzNodeId);
      
      // Position at the bottom of the cloud service
      const serviceSize = cloudServiceSizeMap.get(cloudService.id) || { width: LAYOUT_CONFIG.minCloudServiceWidth, height: LAYOUT_CONFIG.minCloudServiceHeight };
      const defaultPosition = {
        x: cloudServicePadding.left + (idx * (dmzNodeSize.width + 15)),
        y: serviceSize.height - dmzNodeSize.height - 20,
      };

      newNodes.push({
        id: dmzNodeId,
        type: 'dmz',
        position: existingDMZ?.position || (config.position_x != null ? { x: config.position_x, y: config.position_y || defaultPosition.y } : defaultPosition),
        parentId: cloudNodeId,
        extent: 'parent',
        data: {
          id: config.id,
          configId: config.id,
          label: config.label,
          firewallRules: config.rules?.length || 0,
        },
        style: { width: dmzNodeSize.width, height: dmzNodeSize.height },
      } as Node);
    });
  });

  return newNodes;
}
